import type { EmailOpenedPayload, EmailPayload, TimelineEvent } from './types';
import { EVENT_TYPES } from './types';

// Time window for collapsing duplicate email send/receive events (e.g. duplicate imports
// share the same RFC 822 Date header so they land adjacent in the sorted timeline).
const EMAIL_THREAD_COLLAPSE_TIME_WINDOW_MS = 30_000;

// Number of normalized body characters compared when byte-equality fails. The sent-record
// and the teammate-received copy of one message diverge only in the tail (signature, footer,
// quote chain, HTML-vs-plain body slot), so a normalized prefix match — paired with an equal
// participant set — reliably identifies the same message without over-collapsing distinct ones.
const EMAIL_BODY_FUZZY_PREFIX_CHARS = 400;

// Time window for collapsing adjacent email-opened pixels (retry pings vs genuine re-opens
// happen within milliseconds; 2 s gives a buffer without merging desktop + mobile opens).
const EMAIL_OPENED_COLLAPSE_TIME_WINDOW_MS = 2_000;

/**
 * A named merge rule consulted by `collapseTimelineEvents`.
 *
 * Each strategy's `tryMerge` receives two adjacent events (prev, current)
 * and returns the winning event if they should collapse, or `null` to keep both.
 */
export interface MergeStrategy {
  readonly name: string;
  tryMerge(prev: TimelineEvent, current: TimelineEvent): TimelineEvent | null;
}

/** Returns absolute milliseconds between two ISO timestamp strings. NaN if either is unparseable. */
function getTimeDeltaMs(ts1: string, ts2: string): number {
  return Math.abs(new Date(ts1).getTime() - new Date(ts2).getTime());
}

/**
 * Normalize an email body for cross-copy comparison: strip HTML tags (so an
 * HTML-polluted `body_text` compares equal to the clean plain-text copy of the
 * same message), drop plain-text list and emphasis markers, and collapse all
 * whitespace runs to single spaces (so line-ending and hard-wrap differences
 * between the sent and received copies don't matter).
 *
 * The marker stripping exists because the two copies of one message are rendered
 * to plain text by DIFFERENT converters. The received copy carries a real
 * text/plain MIME part (Gmail's rendering: `- item` bullets, `*bold*` emphasis);
 * the sent copy's plain slot is frequently derived from the HTML by our own
 * `html_to_plain_text`, which emits neither. Comparing them raw diverges on the
 * first list item — well inside the prefix window — so a message that is plainly
 * the same on screen fails to collapse. We cannot match Gmail's renderer
 * byte-for-byte, so the comparison absorbs the difference instead.
 *
 * Used ONLY for match comparison, never for display, and applied symmetrically to
 * both sides — so an imperfect strip (e.g. a literal `<` in "x < 5") can only
 * cause a false NON-match, never a false collapse. Markers are removed rather
 * than normalized to a common form for the same reason: dropping them can only
 * make two already-similar bodies compare equal, and collapse additionally
 * requires an equal participant set and a 30 s adjacency window.
 */
function normalizeBodyForMatch(body: string): string {
  return (
    body
      .replace(/<[^>]+>/g, ' ')
      // Leading list / quote markers: "- item", "* item", "• item", "> quoted".
      // Anchored to line start so a mid-sentence hyphen or asterisk is untouched.
      .replace(/^[ \t]*[-*•>]+[ \t]+/gm, '')
      // Wrapping emphasis: *bold*, **bold**, _italic_ → the inner text. Requires a
      // non-space immediately inside the marker, and the closing run to exactly
      // match the opening run (same character, same count) via backreference, so
      // "2 * 3 = 6" and mismatched pairs like "*text_" are left alone.
      .replace(/(^|\s)([*_]{1,2})([^\s*_][^*_]*?)\2(?=[\s.,;:!?)]|$)/gm, '$1$3')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/** True when two email payloads share the same sender and the same recipient set. */
function sameParticipants(
  a: EmailPayload | undefined,
  b: EmailPayload | undefined
): boolean {
  if ((a?.from ?? '') !== (b?.from ?? '')) {
    return false;
  }
  const toA = a?.to ?? [];
  const toB = b?.to ?? [];
  if (toA.length !== toB.length) {
    return false;
  }
  const setB = new Set(toB);
  return toA.every(addr => setB.has(addr));
}

const EMAIL_CONTENT_TYPES = new Set<string>([
  EVENT_TYPES.EMAIL_SENT,
  EVENT_TYPES.EMAIL_RECEIVED,
]);

type ProviderMessageIdentity = { provider: 'gmail' | 'outlook'; id: string };

/**
 * Extract the single provider-assigned message identity from an email payload.
 * Gmail and Outlook ids live in separate fields and separate namespaces, so an
 * event carries at most one. Returns null when neither field holds a non-empty id
 * (legacy events ingested before provider-id capture) — the caller falls back to
 * body comparison.
 */
function getProviderMessageId(
  payload: EmailPayload | undefined
): ProviderMessageIdentity | null {
  const gmailId = payload?.gmail_message_id?.trim();
  if (gmailId) {
    return { provider: 'gmail', id: gmailId };
  }
  const outlookId = payload?.outlook_message_id?.trim();
  if (outlookId) {
    return { provider: 'outlook', id: outlookId };
  }
  return null;
}

/** RFC 822 Message-ID from the payload, trimmed; null when absent/blank. */
function getInternetMessageId(
  payload: EmailPayload | undefined
): string | null {
  const id = payload?.internet_message_id?.trim();
  return id ? id : null;
}

/**
 * Confirms two adjacent sent/received email events are the same underlying message.
 *
 * Three-rung identity ladder:
 *
 * 1. RFC 822 Message-ID (`internet_message_id`) is authoritative in BOTH directions —
 *    equal means same message, different means different messages. A cross-mailbox copy
 *    of one message carries the same Message-ID in every inbox.
 *
 * 2. Provider id is POSITIVE-ONLY — equal ids in the same namespace (gmail/outlook)
 *    confirm the same message; a mismatch is inconclusive because ids are per-mailbox
 *    and a gmail id will never equal an outlook id anyway. Falls through on mismatch.
 *
 * 3. Body comparison (unchanged), including the empty-both-bodies rule that resolves
 *    to sameParticipants.
 */
function isSameMessage(
  prev: EmailPayload | undefined,
  curr: EmailPayload | undefined
): boolean {
  // Rung 1: RFC 822 Message-ID is authoritative in BOTH directions —
  // equal => same message, different => different messages. Stop here.
  const prevImid = getInternetMessageId(prev);
  const currImid = getInternetMessageId(curr);
  if (prevImid !== null && currImid !== null) {
    return prevImid === currImid;
  }
  // Rung 2: provider id is POSITIVE-ONLY — equal (same namespace) => same
  // message; a mismatch is inconclusive (ids are per-mailbox) and falls
  // through. A gmail id never equals an outlook id.
  const prevId = getProviderMessageId(prev);
  const currId = getProviderMessageId(curr);
  if (
    prevId !== null &&
    currId !== null &&
    prevId.provider === currId.provider &&
    prevId.id === currId.id
  ) {
    return true;
  }
  // Rung 3: body comparison (unchanged), incl. its empty-both-bodies rule
  // that resolves to sameParticipants.
  return bodiesMatch(prev, curr);
}

/**
 * Collapses duplicate email sent/received events for the same message within a 30 s window.
 *
 * This arises when a message a teammate is on lands in more than one synced org mailbox: the
 * sender's mailbox records an `ahoy_email_sent` while every other synced recipient mailbox
 * ingests a delivered copy as `ahoy_email_received`, each on its own per-mailbox thread entity.
 * Both thread entities associate to the same contact, so the contact timeline shows both.
 *
 * **Body match — exact OR fuzzy.** The sent record and the received copy of the same message
 * frequently diverge: the sent `body_text` may carry raw HTML (a compose leak) while the
 * received copy is clean plain text, line endings differ (LF vs CRLF), the delivered copy is
 * hard-wrapped, and the signature/footer/quote-chain tails are generated by different code
 * paths. So byte-equality alone misses real duplicates. When it fails, fall back to a fuzzy
 * key: equal subject, equal participant set (same `from` + same `to`), and equal
 * whitespace-and-tag-normalized body PREFIX (the head is user-authored and identical; only the
 * tail diverges). The participant-set requirement keeps the looser body key from over-collapsing
 * two genuinely distinct emails.
 *
 * **SENT-wins:** When exactly one side is EMAIL_SENT, the sent event wins so the timeline
 * shows the authoritative outbound record rather than an inbound echo.
 */
const emailContentStrategy: MergeStrategy = {
  name: 'emailContentStrategy',
  tryMerge(prev, current) {
    if (
      !EMAIL_CONTENT_TYPES.has(prev.event_type_name) ||
      !EMAIL_CONTENT_TYPES.has(current.event_type_name)
    ) {
      return null;
    }

    const prevPayload = prev.payload as EmailPayload | undefined;
    const currPayload = current.payload as EmailPayload | undefined;

    const sameSubject =
      (prevPayload?.subject ?? '') === (currPayload?.subject ?? '');
    const withinWindow =
      getTimeDeltaMs(prev.occurred_at, current.occurred_at) <=
      EMAIL_THREAD_COLLAPSE_TIME_WINDOW_MS;

    if (
      !sameSubject ||
      !withinWindow ||
      !isSameMessage(prevPayload, currPayload)
    ) {
      return null;
    }

    const prevIsSent = prev.event_type_name === EVENT_TYPES.EMAIL_SENT;
    const currIsSent = current.event_type_name === EVENT_TYPES.EMAIL_SENT;

    if (prevIsSent && !currIsSent) {
      return prev;
    }
    if (currIsSent && !prevIsSent) {
      return current;
    }
    return prev;
  },
};

/**
 * Body-equality test for the two copies of one message. Exact `body_text` equality wins
 * outright for non-empty bodies. Otherwise a fuzzy match requires equal participants AND
 * equal normalized body prefix. When BOTH bodies are empty (e.g. two HTML-only messages
 * whose plain-text slot is blank), require an equal participant set rather than collapsing
 * on subject alone — that guards against merging two distinct empty-plain-text emails.
 */
function bodiesMatch(
  prev: EmailPayload | undefined,
  curr: EmailPayload | undefined
): boolean {
  const prevBody = prev?.body_text ?? '';
  const currBody = curr?.body_text ?? '';

  const normPrev = normalizeBodyForMatch(prevBody);
  const normCurr = normalizeBodyForMatch(currBody);

  if (normPrev.length === 0 && normCurr.length === 0) {
    return sameParticipants(prev, curr);
  }
  if (prevBody === currBody) {
    return true;
  }
  return (
    sameParticipants(prev, curr) &&
    normPrev.slice(0, EMAIL_BODY_FUZZY_PREFIX_CHARS) ===
      normCurr.slice(0, EMAIL_BODY_FUZZY_PREFIX_CHARS)
  );
}

/**
 * Collapses adjacent EMAIL_OPENED events within a 2 s window.
 *
 * Tracking pixels are re-requested on redirect (typically < 200 ms apart) or by
 * email pre-fetch services. Genuine desktop-then-mobile re-opens are separated by
 * seconds to minutes, so 2 s avoids false merges while eliminating pixel retries.
 */
const emailOpenedStrategy: MergeStrategy = {
  name: 'emailOpenedStrategy',
  tryMerge(prev, current) {
    if (
      prev.event_type_name !== EVENT_TYPES.EMAIL_OPENED ||
      current.event_type_name !== EVENT_TYPES.EMAIL_OPENED
    ) {
      return null;
    }

    const prevPayload = prev.payload as EmailOpenedPayload | undefined;
    const currPayload = current.payload as EmailOpenedPayload | undefined;

    const withinWindow =
      getTimeDeltaMs(
        prevPayload?.opened_at ?? '',
        currPayload?.opened_at ?? ''
      ) <= EMAIL_OPENED_COLLAPSE_TIME_WINDOW_MS;

    return withinWindow ? prev : null;
  },
};

const EMAIL_SEND_RECEIVE_TYPES = new Set<string>([
  EVENT_TYPES.EMAIL_SENT,
  EVENT_TYPES.EMAIL_RECEIVED,
]);

/**
 * Suppresses an OBJECT_CREATED event that appears adjacent to an email event within 30 s.
 *
 * When a contact is auto-created as a side-effect of sending or receiving an email the
 * timeline would show both the creation notice and the email. The email carries all the
 * meaningful context, so the OBJECT_CREATED entry is redundant.
 */
const emailSupersedesCreatedStrategy: MergeStrategy = {
  name: 'emailSupersedesCreatedStrategy',
  tryMerge(prev, current) {
    const prevIsEmail = EMAIL_SEND_RECEIVE_TYPES.has(prev.event_type_name);
    const currIsEmail = EMAIL_SEND_RECEIVE_TYPES.has(current.event_type_name);
    const prevIsCreated = prev.event_type_name === EVENT_TYPES.OBJECT_CREATED;
    const currIsCreated =
      current.event_type_name === EVENT_TYPES.OBJECT_CREATED;

    const oneEmail =
      (prevIsEmail && currIsCreated) || (currIsEmail && prevIsCreated);
    if (!oneEmail) {
      return null;
    }

    const withinWindow =
      getTimeDeltaMs(prev.occurred_at, current.occurred_at) <=
      EMAIL_THREAD_COLLAPSE_TIME_WINDOW_MS;

    if (!withinWindow) {
      return null;
    }

    return prevIsEmail ? prev : current;
  },
};

/**
 * Ordered list of merge strategies applied by `collapseTimelineEvents`.
 *
 * Strategies are consulted in declaration order; the first non-null result wins
 * (short-circuit). Gates are designed to be disjoint so ordering is for readability only.
 * Register additional strategies here to extend collapse behaviour.
 */
export const MERGE_STRATEGIES: readonly MergeStrategy[] = [
  emailContentStrategy,
  emailOpenedStrategy,
  emailSupersedesCreatedStrategy,
];

/**
 * Collapses adjacent duplicate/redundant timeline events using a registry of merge strategies.
 *
 * Walks the event array with `Array.prototype.reduce` and peek-back. For each event,
 * consults strategies in order; the first non-null result wins (short-circuit).
 *
 * **Adjacency assumption:** Backend sorts events by `-occurred_at` only
 * (`apps/crm_events/api/selectors/event_selectors.py:225`). Duplicate imports share
 * `occurred_at` from the RFC 822 Date header, so collapsable pairs land adjacent.
 * `flatMap(page => page.events)` preserves per-page order.
 *
 * **Extension:** Register a new `MergeStrategy` in `MERGE_STRATEGIES` to handle
 * additional collapse rules. Strategies should have disjoint gates to avoid
 * order-dependent behavior.
 *
 * Pure, O(n), deterministic. Does not mutate the input array.
 */
export function collapseTimelineEvents(
  events: TimelineEvent[],
  strategies: readonly MergeStrategy[] = MERGE_STRATEGIES
): TimelineEvent[] {
  return events.reduce<TimelineEvent[]>((acc, current) => {
    if (acc.length === 0) {
      acc.push(current);
      return acc;
    }

    const prev = acc[acc.length - 1];
    let winner: TimelineEvent | null = null;

    for (const strategy of strategies) {
      const result = strategy.tryMerge(prev, current);
      if (result !== null) {
        winner = result;
        break;
      }
    }

    if (winner !== null) {
      acc[acc.length - 1] = winner;
    } else {
      acc.push(current);
    }

    return acc;
  }, []);
}
